import { Navigate, Route, Routes } from "react-router";
import { RootLayout } from "./components/layout/RootLayout";
import { ProtectedLayout } from "./components/layout/ProtectedLayout";
import { SsoRedirect } from "./SSO/ssoRedirect";
import { SsoLogin } from "./SSO/ssoLogin";
import { SsoError } from "./SSO/ssoError";
import {
  isManualLoginEnabled,
  SSO_ERROR_PATH,
  SSO_LOGIN_ACTION_PATH,
} from "./SSO/ssoConfig";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { Clients } from "./pages/Clients";
import { ClientConfiguration } from "./pages/ClientConfiguration";
import { POJacketManagement } from "./pages/POJacketManagement";
import { BillingProfileConfiguration } from "./pages/BillingProfileConfiguration";
import { ContractsBilling } from "./pages/ContractsBilling";
import { ActivityEntry } from "./pages/ActivityEntry";
import { Invoices } from "./pages/Invoices";
import { DraftInvoiceReview } from "./pages/DraftInvoiceReview";
import { FinalInvoice } from "./pages/FinalInvoice";
import { AccountingAllocation } from "./pages/AccountingAllocation";
import { AccountingAllocationRules } from "./pages/AccountingAllocationRules";
import { InvoiceAllocationDetail } from "./pages/InvoiceAllocationDetail";
import { Reports } from "./pages/Reports";
import { ServiceCatalog } from "./pages/ServiceCatalog";
import { RecentUserActivity } from "./pages/RecentUserActivity";
import { AssignedRolePrivileges } from "./pages/AssignedRolePrivileges";
import { AutomatedInvoiceCreateJob } from "./pages/AutomatedInvoiceCreateJob";
import { CreateNewPrivilege } from "./pages/CreateNewPrivilege";
import { CreateNewRole } from "./pages/CreateNewRole";
import { ServerLogs } from "./pages/ServerLogs";
import { RedirectToDashboard } from "./pages/RedirectToDashboard";
import { PrivilegeRoute } from "./components/layout/PrivilegeRoute";
import {
  PRIVILEGE_CREATE_NEW_PRIVILEGE,
  PRIVILEGE_CREATE_NEW_ROLE,
  PRIVILEGE_VIEW_ASSIGNED_ROLE_PRIVILEGES,
  PRIVILEGE_VIEW_RECENT_USER_ACTIVITY,
  PRIVILEGE_MANUALLY_RUN_INVOICE_CREATION_JOB,
} from "../redux/utils/authPrivileges";

/** Application routes (BrowserRouter + Routes — reliable client-side outlet updates). */
export function AppRoutes() {
  return (
    <Routes>
      <Route element={<RootLayout />}>
        <Route index element={<SsoRedirect />} />
        <Route path={SSO_LOGIN_ACTION_PATH} element={<SsoLogin />} />
        <Route path={SSO_ERROR_PATH} element={<SsoError />} />
        {isManualLoginEnabled() ? (
          <Route path="/login" element={<Login />} />
        ) : (
          <Route path="/login" element={<Navigate to="/" replace />} />
        )}
        <Route element={<ProtectedLayout />}>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="clients" element={<Clients />} />
          <Route path="clients/:id" element={<ClientConfiguration />} />
          <Route path="clients/:id/po-jackets" element={<POJacketManagement />} />
          <Route path="billing-profile" element={<BillingProfileConfiguration />} />
          <Route path="contracts-billing" element={<ContractsBilling />} />
          <Route path="activity-entry" element={<ActivityEntry />} />
          <Route path="invoices" element={<Invoices />} />
          <Route path="invoices/new/review" element={<DraftInvoiceReview />} />
          <Route path="invoices/:id/review" element={<DraftInvoiceReview />} />
          <Route path="invoices/:id/view" element={<FinalInvoice />} />
          <Route path="accounting-allocation" element={<AccountingAllocation />} />
          <Route path="accounting-allocation/rules" element={<AccountingAllocationRules />} />
          <Route path="accounting-allocation/detail" element={<InvoiceAllocationDetail />} />
          <Route path="reports" element={<Reports />} />
          <Route path="service-catalog" element={<ServiceCatalog />} />
          <Route
            path="user-activity"
            element={
              <PrivilegeRoute privilege={PRIVILEGE_VIEW_RECENT_USER_ACTIVITY}>
                <RecentUserActivity />
              </PrivilegeRoute>
            }
          />
          <Route
            path="roles/create"
            element={
              <PrivilegeRoute privilege={PRIVILEGE_CREATE_NEW_ROLE}>
                <CreateNewRole />
              </PrivilegeRoute>
            }
          />
          <Route
            path="role-privileges/create"
            element={
              <PrivilegeRoute privilege={PRIVILEGE_CREATE_NEW_PRIVILEGE}>
                <CreateNewPrivilege />
              </PrivilegeRoute>
            }
          />
          <Route
            path="role-privileges"
            element={
              <PrivilegeRoute privilege={PRIVILEGE_VIEW_ASSIGNED_ROLE_PRIVILEGES}>
                <AssignedRolePrivileges />
              </PrivilegeRoute>
            }
          />
          <Route
            path="admin/server-logs"
            element={
              <PrivilegeRoute privilege={PRIVILEGE_VIEW_ASSIGNED_ROLE_PRIVILEGES}>
                <ServerLogs />
              </PrivilegeRoute>
            }
          />
          <Route
            path="admin/automated-invoice-job"
            element={
              <PrivilegeRoute privilege={PRIVILEGE_MANUALLY_RUN_INVOICE_CREATION_JOB}>
                <AutomatedInvoiceCreateJob />
              </PrivilegeRoute>
            }
          />
          <Route path="admin" element={<RedirectToDashboard />} />
          <Route path="admin/*" element={<RedirectToDashboard />} />
        </Route>
      </Route>
    </Routes>
  );
}
